<template>
    <PtHeaderDropdown
        ref="transDropdown"
        class="lang-switcher th-dropdown th-dropdown-end th-dropdown-open"
        trigger="click"
        :class="{
            'pa-pointer-events-none': disabled,
            'pa-cursor-not-allowed': disabled,
        }"
    >
        <template #reference="{ open }">
            <ElasticButton
                :alwaysNeedTooltip="true"
                :disabled="disabled"
            >
                <template #tipContent>
                    <div v-html="$t('translate-tip')" />
                </template>
                <template #icon>
                    <PtIcon :icon="translateIconName" />
                </template>
                <template #default>
                    <span class="translate-dropdown-title">{{ getOptionLabel(curLang) }}</span>
                    <PtIcon v-if="open" icon="SolidDropdownClose" />
                    <PtIcon v-else icon="SolidDropdownOpen" />
                </template>
            </ElasticButton>
        </template>
        <template #popper="{ close }">
            <ul class="th-dropdown-content th-dropdown-content__shadow th-menu pa-w-44 pa-mt-1">
                <template v-for="menuItem in switchOptions">
                    <li
                        v-if="!menuItem.lang"
                        :key="menuItem.label"
                        class="lang-switcher-group-title !pa-text-xs !pa-leading-4 !pa-mb-0"
                    >
                        <span>
                            {{ menuItem.label }}
                        </span>
                    </li>
                    <li
                        v-else
                        :key="menuItem.lang"
                        :class="`pendo-track__function-bar__translate-${menuItem.lang}`"
                        class="pa-flex pa-justify-between"
                        @click="() => setTransLang(menuItem.lang, menuItem.langType, close)"
                    >
                        <span>{{ menuItem.label }}</span>
                        <ElPopover
                            v-if="menuItem.tip"
                            placement="bottom"
                            trigger="hover"
                            :width="500"
                            :visibleArrow="false"
                        >
                            <template #reference>
                                <PtIcon
                                    icon="SolidInfo"
                                    class="!pa-fill-grey-60"
                                />
                            </template>
                            <span v-html="menuItem.tip" />
                        </ElPopover>
                    </li>
                </template>
            </ul>
        </template>
    </PtHeaderDropdown>
</template>

<script setup lang="ts">
import '@patsnap-ui/icon/assets/solid/DropdownOpen.svg';
import '@patsnap-ui/icon/assets/solid/TranslateChinese.svg';
import '@patsnap-ui/icon/assets/solid/TranslateEnglish.svg';
import '@patsnap-ui/icon/assets/solid/info.svg';

import { useI18n } from '@/composables/useI18n';
import { useTranslationStates } from '@/composables/useTranslationStates';
import { DEFAULT_LANGUAGE, LANG_MODE_DEFAULT, LANG_MODE_ORIGINAL, } from '@/constants';
import type { PatentData } from '@/services/patent-service.interface';
import type { LangTypeMode, Language, Side } from '@/services/translation/translate-service.interface';
import fileWrapperMf from '@/subview_plugins/FireWrapper/manifest';
import customsMf from '@/subview_plugins/LegalCustoms/manifest';
import rerexamMf from '@/subview_plugins/LegalRerexam/manifest';
import legalStatusMf from '@/subview_plugins/LegalStatus/manifest';
import licensingMf from '@/subview_plugins/Licensing/manifest';
import litigationMf from '@/subview_plugins/Litigation/manifest';
import imageMf from '@/subview_plugins/Image/manifest';
import literatureMf from '@/subview_plugins/Literature/manifest';
import pdfMf from '@/subview_plugins/Pdf/manifest';
import pledgeMf from '@/subview_plugins/Pledge/manifest';
import valuationMf from '@/subview_plugins/valuation/manifest';
import { Popover as ElPopover } from 'element-ui';
import _ from 'lodash';
import { HeaderDropdown as PtHeaderDropdown, Icon as PtIcon } from 'patsnap-biz';
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router/composables';
import ElasticButton from '../ElasticButton.vue';
import { useLangSwitchOptions } from './useLangSwitchOptions';
import { type OriginOption } from './useOriginOptions';
import { useCompareUrlState } from '@/composables/useCompareUrlState';
import { dualViewLangsTab } from '@/services/dual-view-langs-tab';

const excludeTranslateSet = new Set([
    valuationMf.name,
    legalStatusMf.name,
    litigationMf.name,
    rerexamMf.name,
    licensingMf.name,
    pledgeMf.name,
    fileWrapperMf.name,
    customsMf.name,
    pdfMf.name,
    imageMf.name,
    literatureMf.name,
]);

const props = withDefaults(defineProps<{
    patentId: string;
    patent: Partial<PatentData>;
    side?: Side;
}>(), {
    side: 'left',
});

const {
    currentTranslateLanguages,
    currentLanguageModes,
    userPreferredLangOptions,
    switchTranslateLanguage,
    switchMachineTrans,
    setUserPreferredLangOptions,
    setSideTransStatus,
} = useTranslationStates();

const { isCompareView } = useCompareUrlState();

const { locale, } = useI18n();

const rootLang = computed(() => {
    return currentTranslateLanguages.value[props.side || 'left'];
});

const curLang = computed(() => {
    return (rootLang.value || '').toUpperCase();
});

const rootLangType = computed(() => {
    return currentLanguageModes.value[props.side || 'left'];
});

const userPreferredLangOption = computed<{ lang: string; langType: LangTypeMode; }>(() => {
    return userPreferredLangOptions.value[props.side || 'left'];
});

const translateIconName = computed(() => {
    const lang = locale.value.toUpperCase();
    return lang === 'EN' ? 'SolidTranslateChinese' : 'SolidTranslateEnglish';
});

const reactivePatent = ref<Partial<PatentData>>({});

watch(() => props.patent, (data) => {
    reactivePatent.value = data as Partial<PatentData>;
});

const {
    originOptions,
    switchOptions,
    getOptionLabel,
} = useLangSwitchOptions(reactivePatent);

const userPreferIsDiffer = computed(() => {
    return rootLang.value !== userPreferredLangOption.value.lang
        && rootLangType.value !== userPreferredLangOption.value.langType;
});

const route = useRoute();

const disabled = computed(() => {
    return excludeTranslateSet.has(route.meta?.pluginName);
});

const sideIdentifier = computed(() => {
    return 'both';
});

watch(originOptions, (texts: OriginOption[]) => {
    if (_.isEmpty(props.patent)) {
        return;
    }
    // 只需要在 LANG_MODE_ORIGINAL 时判断是不是依旧有这个 rootLang, 没有则全部重制成默认('NON', LANG_MODE_DEFAULT)
    if (userPreferredLangOption.value.langType === LANG_MODE_ORIGINAL) {
        const availableOrigins = new Set(texts.map((origin) => origin.type));
        if (availableOrigins.has(userPreferredLangOption.value.lang)) {
            if (userPreferIsDiffer.value) {
                switchTranslateLanguage({
                    side: sideIdentifier.value,
                    lang: userPreferredLangOption.value.lang,
                    langType: userPreferredLangOption.value.langType,
                });
            }
        } else {
            switchTranslateLanguage({
                side: sideIdentifier.value,
                lang: DEFAULT_LANGUAGE,
                langType: LANG_MODE_DEFAULT,
            });
        }
    }
});

function setTransLang(lang: Language, langType: LangTypeMode, close: () => void) {
    if (lang === DEFAULT_LANGUAGE) {
        dualViewLangsTab('left').setCacheValue(true);
        dualViewLangsTab('right').setCacheValue(true);
    }
    switchTranslateLanguage({
        side: sideIdentifier.value,
        lang,
        langType,
    });

    switchMachineTrans({
        side: sideIdentifier.value,
        status: false
    });

    setSideTransStatus({
        side: (isCompareView.value ? props.side : undefined),
        val: (lang !== 'NON')
    });

    setUserPreferredLangOptions({
        side: sideIdentifier.value,
        lang,
        langType,
    });

    window.rvTrack('Translate Toggle', { transLang: lang });
    close();
}
</script>

<style lang="scss">
.lang-switcher-group-title {
    @apply th-menu-item-disabled pa-border-t-[1px] pa-border-grey-60 pa-p-2 pa-mx-[-8px] pa-w-[calc(100%+16px)] pa-pointer-events-none;
}
</style>
